import { useEffect, useRef, useState } from "react";

const stats = [
  { number: "15+", label: "Years Experience", icon: "ri-medal-line" },
  { number: "5,000+", label: "Families Covered", icon: "ri-group-line" },
  { number: "50+", label: "Top Carriers", icon: "ri-building-line" },
  { number: "24/7", label: "Expert Support", icon: "ri-customer-service-2-line" },
];

export default function AboutSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-24 bg-stone-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-['Open_Sans'] font-600 tracking-widest uppercase mb-4">
            Who We Are
          </span>
          <h2 className="font-['Poppins'] font-700 text-4xl lg:text-5xl text-emerald-900 mb-4">
            About HealthMarkets<br />Insurance Agency
          </h2>
          <p className="font-['Open_Sans'] text-gray-500 max-w-xl mx-auto text-base leading-relaxed">
            Serving the Irving, TX community with trusted private health insurance guidance since day one.
          </p>
        </div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Left: Text */}
          <div className={`transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <img
              src="https://public.readdy.ai/ai/img_res/62e91b020b794a5c3f7277415a8fef99.jpg"
              alt="HealthMarkets Insurance Agency team in Irving TX"
              title="Our licensed health insurance agents serving Irving, TX"
              className="w-full h-72 object-cover object-top rounded-lg mb-8"
            />
            <p className="font-['Open_Sans'] text-gray-600 text-base leading-relaxed mb-5">
              At <strong>HealthMarkets Insurance Agency</strong> in Irving, TX, we believe every family deserves 
              access to quality, affordable <strong>private health insurance</strong> plans. Our licensed and 
              certified agents work tirelessly to match each client with the coverage that fits their life.
            </p>
            <p className="font-['Open_Sans'] text-gray-600 text-base leading-relaxed mb-5">
              With access to <strong>50+ top insurance carriers</strong>, we offer a comprehensive range of 
              individual, family, Medicare, and supplemental <strong>health insurance plans</strong>. We're 
              independent agents — which means we shop multiple carriers to find your best rate.
            </p>
            <p className="font-['Open_Sans'] text-gray-600 text-base leading-relaxed">
              Based at <strong>1800 Market Pl Blvd #140, Irving, TX 75063</strong>, our doors are open 
              24 hours a day, 7 days a week. Whatever your situation, call us at{" "}
              <a href="tel:9722174662" className="text-emerald-700 font-600 cursor-pointer">972-217-4662</a>{" "}
              and we'll find a solution together.
            </p>
          </div>

          {/* Right: Info Card + Map */}
          <div className={`transition-all duration-700 delay-300 ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
            <div className="bg-white border border-emerald-100 rounded-lg p-8 mb-6">
              <h3 className="font-['Poppins'] font-600 text-lg text-emerald-900 mb-6">Agency Information</h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-line text-emerald-700 text-lg"></i>
                  </div>
                  <div>
                    <div className="font-['Open_Sans'] font-600 text-gray-800 text-sm">Our Location</div>
                    <div className="font-['Open_Sans'] text-gray-500 text-sm">1800 Market Pl Blvd #140<br />Irving, TX 75063</div>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-amber-600 text-lg"></i>
                  </div>
                  <div>
                    <div className="font-['Open_Sans'] font-600 text-gray-800 text-sm">Business Hours</div>
                    <div className="font-['Open_Sans'] text-emerald-700 font-600 text-sm">Open 24 Hours, 7 Days a Week</div>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-emerald-700 text-lg"></i>
                  </div>
                  <div>
                    <div className="font-['Open_Sans'] font-600 text-gray-800 text-sm">Phone Number</div>
                    <a href="tel:9722174662" className="font-['Open_Sans'] text-emerald-700 font-600 text-sm cursor-pointer hover:text-emerald-900">
                      972-217-4662
                    </a>
                  </div>
                </li>
              </ul>
              <a
                href="https://maps.app.goo.gl/grzX3dievhN3giBv8"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-6 flex items-center justify-center gap-2 w-full py-3 border-2 border-emerald-700 text-emerald-700 rounded-lg font-['Poppins'] font-600 text-sm hover:bg-emerald-700 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-map-2-line"></i>
                Get Directions on Google Maps
              </a>
            </div>

            {/* Google Map Embed */}
            <div className="rounded-lg overflow-hidden border border-emerald-100 h-52">
              <iframe
                title="HealthMarkets Insurance Agency location map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3351.3!2d-96.9464!3d32.8784!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e83b0a4f49b4d%3A0x5b2e1aee16e4b9e0!2s1800%20Market%20Pl%20Blvd%20%23140%2C%20Irving%2C%20TX%2075063!5e0!3m2!1sen!2sus!4v1711000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white border border-emerald-100 rounded-lg p-6 text-center">
              <div className="w-12 h-12 rounded-lg bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-emerald-700 text-xl`}></i>
              </div>
              <div className="font-['Poppins'] font-800 text-3xl text-emerald-900 mb-1">{stat.number}</div>
              <div className="font-['Open_Sans'] text-gray-500 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
